import React, { createContext, useContext, ReactNode } from 'react';
import { useWatchlistManager } from '../hooks/useWatchlistManager';
import { useWatchlistStore } from '../stores';
import type {
  CompanyWithNews,
  IndustryWithNews,
  SelectedCompany,
  SelectedIndustry,
} from '../types';

interface AppContextType {
  // From watchlist store
  selectedCompany: SelectedCompany | null;
  selectedIndustry: SelectedIndustry | null;
  showIndustrySelector: boolean;
  setSelectedCompany: (company: SelectedCompany | null) => void;
  setSelectedIndustry: (industry: SelectedIndustry | null) => void;
  setShowIndustrySelector: (show: boolean) => void;
  
  // From watchlist manager
  watchedCompanies: CompanyWithNews[];
  watchedIndustries: IndustryWithNews[];
  watchlistInitialized: boolean;
  refreshWatchlist: () => Promise<void>;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
};

interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  // Get state from stores and managers
  const {
    selectedCompany,
    selectedIndustry,
    showIndustrySelector,
    setSelectedCompany,
    setSelectedIndustry,
    setShowIndustrySelector,
  } = useWatchlistStore();

  const {
    watchedCompanies,
    watchedIndustries,
    watchlistInitialized,
    refreshWatchlist,
  } = useWatchlistManager();
  
  const value: AppContextType = {
    // Store states
    selectedCompany,
    selectedIndustry,
    showIndustrySelector,
    setSelectedCompany,
    setSelectedIndustry,
    setShowIndustrySelector,
    
    // Manager data
    watchedCompanies,
    watchedIndustries,
    watchlistInitialized,
    refreshWatchlist,
  };
  
  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};